import {
  Address,
  BASE_FEE,
  Contract,
  Networks,
  TransactionBuilder,
  nativeToScVal,
} from '@stellar/stellar-sdk';
import { Server } from '@stellar/stellar-sdk/rpc';
import { DemoFixture, Snapshot } from './demoData';

export const TESTNET_RPC_URL = 'https://soroban-testnet.stellar.org';
export const TESTNET_NETWORK_PASSPHRASE = Networks.TESTNET;
const DEMO_ONCHAIN_SETUP_PATH = '/demo-onchain/setup.json';

export type RpcPublishForm = {
  rpcUrl: string;
  networkPassphrase: string;
  registryContractId: string;
  verifierContractId: string;
  subjectAddress: string;
  verificationKeyJson: string;
  proofJson: string;
  publicSignalsJson: string;
};

export type RpcPublishResult = {
  hash: string;
  latestLedger?: number;
  status: string;
};

export type WalletTransactionSigner = (
  transactionXdr: string,
  opts: { address: string; networkPassphrase: string },
) => Promise<string>;

type SorobanVerificationKey = {
  alpha: Uint8Array;
  beta: Uint8Array;
  gamma: Uint8Array;
  delta: Uint8Array;
  ic: Uint8Array[];
};

type SorobanGroth16Proof = {
  a: Uint8Array;
  b: Uint8Array;
  c: Uint8Array;
};

type DemoOnchainSetupManifest = {
  rpcUrl: string;
  networkPassphrase: string;
  registryContractId: string;
  verifierContractId: string;
  subjectAddress: string;
  verificationKeyPath: string;
  proofPath: string;
  publicSignalsPath: string;
  fixturePath: string;
};

export type DemoOnchainSetup = {
  fixture: unknown;
  form: RpcPublishForm;
};

export function defaultRpcPublishForm(): RpcPublishForm {
  return {
    rpcUrl: TESTNET_RPC_URL,
    networkPassphrase: TESTNET_NETWORK_PASSPHRASE,
    registryContractId: '',
    verifierContractId: '',
    subjectAddress: '',
    verificationKeyJson: '',
    proofJson: '',
    publicSignalsJson: '',
  };
}

export async function loadDemoOnchainSetup(current: RpcPublishForm): Promise<DemoOnchainSetup> {
  const manifest = await fetchJson<DemoOnchainSetupManifest>(DEMO_ONCHAIN_SETUP_PATH);
  const [verificationKeyJson, proofJson, publicSignalsJson, fixture] = await Promise.all([
    fetchText(manifest.verificationKeyPath),
    fetchText(manifest.proofPath),
    fetchText(manifest.publicSignalsPath),
    fetchJson<unknown>(manifest.fixturePath),
  ]);

  return {
    fixture,
    form: {
      ...current,
      rpcUrl: manifest.rpcUrl,
      networkPassphrase: manifest.networkPassphrase,
      registryContractId: manifest.registryContractId,
      verifierContractId: manifest.verifierContractId,
      subjectAddress: manifest.subjectAddress || current.subjectAddress,
      verificationKeyJson,
      proofJson,
      publicSignalsJson,
    },
  };
}

export async function requestWalletAirdrop(
  form: RpcPublishForm,
  publicKey: string,
): Promise<string> {
  assertTestnet(form);
  const server = rpcServer(form.rpcUrl);
  const account = await fundAndLoadAccount(server, required(publicKey, 'Wallet address'));
  return account.accountId();
}

export async function publishReserveSnapshotViaRpc(
  fixture: DemoFixture,
  form: RpcPublishForm,
  sourcePublic: string,
  signTransactionXdr: WalletTransactionSigner,
): Promise<RpcPublishResult> {
  assertTestnet(form);

  const server = rpcServer(form.rpcUrl);
  const sourceAccount = await getWalletAccount(server, sourcePublic);
  const contract = new Contract(required(form.registryContractId, 'Registry contract ID'));
  const operation = contract.call(
    'publish',
    Address.fromString(sourcePublic).toScVal(),
    Address.fromString(required(form.subjectAddress, 'Subject address')).toScVal(),
    Address.fromString(required(form.verifierContractId, 'Verifier contract ID')).toScVal(),
    snapshotToScVal(fixture.snapshot),
    verificationKeyToScVal(parseVerificationKey(form.verificationKeyJson)),
    proofToScVal(parseProof(form.proofJson)),
    publicSignalsToScVal(parsePublicSignals(form.publicSignalsJson, fixture.snapshot)),
  );

  const transaction = new TransactionBuilder(sourceAccount, {
    fee: BASE_FEE,
    networkPassphrase: form.networkPassphrase,
  })
    .addOperation(operation)
    .setTimeout(60)
    .build();

  const preparedTransaction = await server.prepareTransaction(transaction);
  const signedXdr = await signTransactionXdr(preparedTransaction.toXDR(), {
    address: sourcePublic,
    networkPassphrase: form.networkPassphrase,
  });
  const signedTransaction = TransactionBuilder.fromXDR(signedXdr, form.networkPassphrase);

  const submitted = await server.sendTransaction(signedTransaction);
  if (submitted.status !== 'PENDING') {
    const errorDetail = submitted.errorResult ? ` (${submitted.errorResult.toString()})` : '';
    throw new Error(
      `RPC rejected transaction: ${submitted.status}${errorDetail}`,
    );
  }

  const finalStatus = await server.pollTransaction(submitted.hash, {
    attempts: 12,
    sleepStrategy: () => 1000,
  });

  if (finalStatus.status !== 'SUCCESS') {
    throw new Error(`Transaction ${submitted.hash} finished with status ${finalStatus.status}`);
  }

  return {
    hash: submitted.hash,
    latestLedger: finalStatus.latestLedger,
    status: finalStatus.status,
  };
}

function rpcServer(rpcUrl: string) {
  const trimmed = required(rpcUrl, 'RPC URL');
  return new Server(trimmed, { allowHttp: trimmed.startsWith('http://') });
}

async function getWalletAccount(server: Server, publicKey: string) {
  try {
    return await server.getAccount(publicKey);
  } catch {
    throw new Error('Connected wallet is not funded on testnet. Open the wallet menu and fund it.');
  }
}

async function fundAndLoadAccount(server: Server, publicKey: string) {
  try {
    return await server.getAccount(publicKey);
  } catch {
    await server.requestAirdrop(publicKey);
    return server.getAccount(publicKey);
  }
}

function snapshotToScVal(snapshot: Snapshot) {
  return nativeToScVal(
    {
      assets: BigInt(snapshot.assets),
      circuit_id: hexToBytes(snapshot.circuit_id, 32, 'snapshot.circuit_id'),
      liabilities: BigInt(snapshot.liabilities),
      proof_hash: hexToBytes(snapshot.proof_hash, 32, 'snapshot.proof_hash'),
      report_hash: hexToBytes(snapshot.report_hash, 32, 'snapshot.report_hash'),
      root_hash: hexToBytes(snapshot.root_hash, 32, 'snapshot.root_hash'),
    },
    {
      type: {
        assets: ['symbol', 'i128'],
        circuit_id: ['symbol', 'bytes'],
        liabilities: ['symbol', 'i128'],
        proof_hash: ['symbol', 'bytes'],
        report_hash: ['symbol', 'bytes'],
        root_hash: ['symbol', 'bytes'],
      },
    },
  );
}

function verificationKeyToScVal(vk: SorobanVerificationKey) {
  return nativeToScVal(
    {
      alpha: vk.alpha,
      beta: vk.beta,
      delta: vk.delta,
      gamma: vk.gamma,
      ic: vk.ic,
    },
    {
      type: {
        alpha: ['symbol', 'bytes'],
        beta: ['symbol', 'bytes'],
        delta: ['symbol', 'bytes'],
        gamma: ['symbol', 'bytes'],
        ic: ['symbol', null],
      },
    },
  );
}

function proofToScVal(proof: SorobanGroth16Proof) {
  return nativeToScVal(
    {
      a: proof.a,
      b: proof.b,
      c: proof.c,
    },
    {
      type: {
        a: ['symbol', 'bytes'],
        b: ['symbol', 'bytes'],
        c: ['symbol', 'bytes'],
      },
    },
  );
}

function publicSignalsToScVal(signals: bigint[]) {
  return nativeToScVal(signals, { type: 'u256' });
}

function parseVerificationKey(text: string): SorobanVerificationKey {
  const value = parseJsonRecord(text, 'verification key');
  if ('alpha' in value) {
    const ic = assertArray(value.ic, 'verification key ic');
    return {
      alpha: parseHexField(value.alpha, 64, 'verification key alpha'),
      beta: parseHexField(value.beta, 128, 'verification key beta'),
      delta: parseHexField(value.delta, 128, 'verification key delta'),
      gamma: parseHexField(value.gamma, 128, 'verification key gamma'),
      ic: ic.map((point, index) => parseHexField(point, 64, `verification key ic[${index}]`)),
    };
  }

  if ('vk_alpha_1' in value) {
    const ic = assertArray(value.IC, 'verification key IC');
    return {
      alpha: snarkG1(value.vk_alpha_1, 'vk_alpha_1'),
      beta: snarkG2(value.vk_beta_2, 'vk_beta_2'),
      delta: snarkG2(value.vk_delta_2, 'vk_delta_2'),
      gamma: snarkG2(value.vk_gamma_2, 'vk_gamma_2'),
      ic: ic.map((point, index) => snarkG1(point, `IC[${index}]`)),
    };
  }

  throw new Error('Verification key JSON must be Soroban-encoded or snarkjs verification_key.json');
}

function parseProof(text: string): SorobanGroth16Proof {
  const value = parseJsonRecord(text, 'proof');
  if ('a' in value) {
    return {
      a: parseHexField(value.a, 64, 'proof a'),
      b: parseHexField(value.b, 128, 'proof b'),
      c: parseHexField(value.c, 64, 'proof c'),
    };
  }

  if ('pi_a' in value) {
    return {
      a: snarkG1(value.pi_a, 'pi_a'),
      b: snarkG2(value.pi_b, 'pi_b'),
      c: snarkG1(value.pi_c, 'pi_c'),
    };
  }

  throw new Error('Proof JSON must be Soroban-encoded or snarkjs proof.json');
}

function parsePublicSignals(text: string, snapshot: Snapshot): bigint[] {
  const fallback = [BigInt(`0x${snapshot.root_hash}`), BigInt(snapshot.assets)];
  if (!text.trim()) {
    return fallback;
  }

  const value = parseJson(text, 'public signals');
  const signals = assertArray(value, 'public signals').map((item, index) =>
    parseIntegerLike(item, `public signal ${index}`),
  );
  if (signals.length < 2) {
    throw new Error('Public signals must include root hash and assets');
  }
  return signals;
}

function snarkG1(value: unknown, label: string): Uint8Array {
  const point = assertArray(value, label);
  return concatBytes([
    decimalToFixedBytes(point[0], 32, `${label}[0]`),
    decimalToFixedBytes(point[1], 32, `${label}[1]`),
  ]);
}

function snarkG2(value: unknown, label: string): Uint8Array {
  const point = assertArray(value, label);
  const x = assertArray(point[0], `${label}[0]`);
  const y = assertArray(point[1], `${label}[1]`);
  // snarkjs emits Fp2 coordinates as [c0, c1]. Soroban BN254 expects c1 || c0.
  return concatBytes([
    decimalToFixedBytes(x[1], 32, `${label}[0][1]`),
    decimalToFixedBytes(x[0], 32, `${label}[0][0]`),
    decimalToFixedBytes(y[1], 32, `${label}[1][1]`),
    decimalToFixedBytes(y[0], 32, `${label}[1][0]`),
  ]);
}

function parseHexField(value: unknown, bytes: number, label: string): Uint8Array {
  if (typeof value !== 'string') {
    throw new Error(`${label} must be a hex string`);
  }
  return hexToBytes(value, bytes, label);
}

function hexToBytes(value: string, bytes: number, label: string): Uint8Array {
  const hex = value.trim().replace(/^0x/i, '').toLowerCase();
  if (!/^[0-9a-f]+$/.test(hex) || hex.length !== bytes * 2) {
    throw new Error(`${label} must be ${bytes} bytes of hex`);
  }
  const output = new Uint8Array(bytes);
  for (let index = 0; index < bytes; index += 1) {
    output[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return output;
}

function decimalToFixedBytes(value: unknown, bytes: number, label: string): Uint8Array {
  let integer = parseIntegerLike(value, label);
  if (integer < 0n) {
    throw new Error(`${label} must be non-negative`);
  }
  const output = new Uint8Array(bytes);
  for (let index = bytes - 1; index >= 0; index -= 1) {
    output[index] = Number(integer & 0xffn);
    integer >>= 8n;
  }
  if (integer !== 0n) {
    throw new Error(`${label} does not fit in ${bytes} bytes`);
  }
  return output;
}

function parseIntegerLike(value: unknown, label: string): bigint {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return BigInt(value);
  }
  if (typeof value !== 'string') {
    throw new Error(`${label} must be an integer string`);
  }
  const trimmed = value.trim();
  if (/^0x[0-9a-f]+$/i.test(trimmed)) {
    return BigInt(trimmed);
  }
  if (/^\d+$/.test(trimmed)) {
    return BigInt(trimmed);
  }
  throw new Error(`${label} must be an integer string`);
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const output = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

function parseJsonRecord(text: string, label: string): Record<string, unknown> {
  return assertRecord(parseJson(required(text, `${label} JSON`), label), label);
}

function parseJson(text: string, label: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'invalid JSON';
    throw new Error(`Unable to parse ${label} JSON: ${message}`);
  }
}

function assertRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} must be a JSON object`);
  }
  return value as Record<string, unknown>;
}

function assertArray(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${label} must be an array`);
  }
  return value;
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Unable to load ${path}`);
  }
  return response.json() as Promise<T>;
}

async function fetchText(path: string): Promise<string> {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Unable to load ${path}`);
  }
  return response.text();
}

function assertTestnet(form: RpcPublishForm) {
  if (form.networkPassphrase !== TESTNET_NETWORK_PASSPHRASE) {
    throw new Error('This browser wallet demo is configured for Stellar testnet');
  }
}

function required(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${label} is required`);
  }
  return trimmed;
}
